import { _, type TranslateService } from '@ngx-translate/core';
import type { NoticeVariant } from '@sb/ui/notice';
import type { SelectItem } from '@sb/ui/select';
import type { PlainDate } from '@shiftbase-com/ts-shared/utilities';

export interface TemplateVariable {
  variable: string;
  type: TemplateVariableType;
  label: string;
  // Compact alternative to `label`, used by the pending chips and by a grouped row's column heads.
  short_label?: string;
  mandatory: boolean;
  internal: boolean;
  custom?: boolean;
  description?: string;
  options?: {
    searchable?: boolean;
    items?: Array<VariableOptionItem | SelectItem<string>>;
    size?: TemplateVariableSize;
    // Renders this field inside the named field's form-field rather than in its own row.
    attachedTo?: string;
    // Overrides the control a `select` renders as.
    display?: 'radio';
    // Value the control starts on when the stored answers hold none.
    default?: string;
    // Fields sharing a group render as one block, ordered by `position`.
    group?: string;
    position?: number;
    helpText?: {
      text: string;
      link?: {
        text: string;
        urls: { [key: string]: string };
      };
      additionalNote?: string;
    };
    maxDate?: PlainDate;
    minDate?: PlainDate;
    // Live bound against another field in the same form; the renderer resolves it
    // against that field's current value. Static minDate/maxDate stay as served.
    dateBoundary?: DateBoundary;
    minlength?: number;
    maxlength?: number;
    // Cap for a `type: 'file_collection'` multi-upload field; falls back to MAX_UPLOAD_FILES.
    maxFiles?: number;
    // Row shape for `type: 'children'`; answers stored as `children.N.<variable>`.
    // `mandatory` is per sub-field and BE-driven; when omitted the sub-field is
    // optional.
    fields?: Array<{ variable: string; type: TemplateVariableType; label: string; mandatory?: boolean }>;
  };
  // Section grouping for the sectioned external form; `description` renders
  // as the helper sentence under the section title. `condition` is the
  // section-level show/hide condition, copied onto every field of a
  // conditional section so the visibility cascade can evaluate it per field.
  category: { key: string; label: string; description?: string; condition?: FieldConditionDto };
  condition?: FieldConditionDto;
}

export type DateBoundaryType = 'after' | 'after_or_equal' | 'before' | 'before_or_equal';

export interface DateBoundary {
  field: string;
  type: DateBoundaryType;
}

export type FieldConditionDto =
  | { type: 'answer_is_one_of'; field: string; values: string[] }
  | { type: 'answer_is_not_one_of'; field: string; values: string[] }
  | { type: 'age_under'; field: string; years: number; reference_date?: string }
  | { type: 'age_at_least'; field: string; years: number; reference_date?: string }
  | { type: 'all_of'; conditions: FieldConditionDto[] };

// Section metadata from the questions endpoint envelope. Only the section
// `condition` is read here; the fields come from the unwrapped `data` array.
export interface FormQuestionsMeta {
  sections?: Array<{ key: string; label: string; position: number; condition?: FieldConditionDto }>;
}

export type TemplateVariableType =
  | 'text'
  | 'numeric'
  | 'decimal'
  | 'money'
  | 'date'
  | 'boolean'
  | 'select'
  | 'multi-select'
  | 'number'
  | 'email'
  | 'phone'
  | 'file'
  | 'file_collection'
  | 'children';
export type TemplateVariableSize = 'sm' | 'md' | 'lg';

export interface TemplateValidationResult {
  id: string;
  title: string;
  eSignEnabled: boolean;
  // Placeholder groups still needed for e-sign; any one placeholder per group
  // satisfies it. Absent on backends that don't return it yet.
  missingESignFields?: string[][];
  variables: TemplateVariable[];
  invalidVariables: string[];
  guardianSignatureCount?: number;
  /** @deprecated Back-compat during rollout; derive from guardianSignatureCount. Drop with SHB-20703. */
  requiresGuardianSignature?: boolean;
}

export type ESignField = Pick<TemplateVariable, 'type' | 'label' | 'variable'>;

// Labels/types for the e-sign anchor placeholders, keyed by placeholder name.
// `label` is a raw ngx-translate key — translate at point of use.
export const E_SIGN_FIELDS: Record<string, ESignField> = {
  signature_employee: { variable: 'signature_employee', type: 'text', label: _('Signature Employee') },
  signature_employer: { variable: 'signature_employer', type: 'text', label: _('Signature Employer') },
  signing_date_employee: { variable: 'signing_date_employee', type: 'date', label: _('Signing Date Employee') },
  signing_date_employer: { variable: 'signing_date_employer', type: 'date', label: _('Signing Date Employer') },
  signature_guardian: { variable: 'signature_guardian', type: 'text', label: _('Signature Guardian') },
  signing_date_guardian: { variable: 'signing_date_guardian', type: 'date', label: _('Signing Date Guardian') },
  signature_guardian_2: { variable: 'signature_guardian_2', type: 'text', label: _('Signature Guardian 2') },
  signing_date_guardian_2: { variable: 'signing_date_guardian_2', type: 'date', label: _('Signing Date Guardian 2') },
};

export function toTranslatedESignField(placeholderName: string, translateService: TranslateService): ESignField {
  const eSignField = E_SIGN_FIELDS[placeholderName];

  // Unknown placeholder: the backend may know anchors this map doesn't yet.
  if (!eSignField) {
    return { variable: placeholderName, type: 'text', label: placeholderName };
  }

  return { ...eSignField, label: translateService.instant(eSignField.label) };
}

export interface TemplateContract {
  id?: string;
  title: string;
  category?: TemplateCategory;
  eSignEnabled?: boolean;
  created?: string;
  guardianSignatureCount?: number;
  /** @deprecated Back-compat during rollout; derive from guardianSignatureCount. Drop with SHB-20703. */
  requiresGuardianSignature?: boolean;
}

export interface FileUploadResult {
  resource_id: string;
}

export interface VariableOptionItem {
  value: string;
  text: string;
}

export interface CreateTemplateVariableData {
  label: string;
  variable: string;
  description?: string;
  type: TemplateVariableType;
  category: string;
  mandatory?: boolean;
  internal: boolean;
  options?: Array<string>;
}

export interface TemplateUsingVariable {
  id: string;
  title: string;
}

export interface GroupedSummaryFields {
  label: string;
  fields: Array<{ label: string; value?: string; variable: string; size?: string }>;
}

export interface GroupedVariables {
  label: string;
  fields: Array<TemplateVariable>;
  notice?: {
    variant: NoticeVariant;
    text: string;
  };
}

export enum TemplateCategory {
  HIRE = 'new_hire',
  EXTENSION = 'extension',
}
